import { useCallback, useEffect, useRef, useState } from "react";
import { Badge, Button, Card, Input } from "@omnichannel/ui";
import { ApiError, request } from "./api";
import type { Tariff } from "./api";

const MAX_TARIFF_PRICE = 9_000_000_000_000_000;

interface PendingCreateRecovery {
  appId: string;
  requestId: number;
  code: string;
  name: string;
  price: number;
  currency: string;
  preexistingIds: string[] | null;
}

interface PendingEditRecovery {
  appId: string;
  tariffId: string;
  requestId: number;
  name: string;
  price: number;
  currency: string;
  is_active: boolean;
  reason: string;
  retryAllowed: boolean;
}

interface PendingDeleteRecovery {
  appId: string;
  tariffId: string;
  reason: string;
  retryAllowed: boolean;
}

export function formatPrice(price: number | null | undefined, currency: string): string {
  if (price === null || price === undefined) return "Бесплатно";
  return `${price} ${currency}`;
}

export function isValidTariff(item: unknown): item is Tariff {
  if (!item || typeof item !== "object" || Array.isArray(item)) return false;
  const t = item as Record<string, unknown>;
  return (
    typeof t.id === "string" &&
    t.id.trim() !== "" &&
    typeof t.code === "string" &&
    t.code.trim() !== "" &&
    typeof t.name === "string" &&
    t.name.trim() !== "" &&
    (t.price === null || (
      typeof t.price === "number" && Number.isSafeInteger(t.price) &&
      t.price >= 0 && t.price <= MAX_TARIFF_PRICE
    )) &&
    typeof t.currency === "string" &&
    t.currency.trim() !== "" &&
    typeof t.is_active === "boolean"
  );
}

export function Tariffs({ appId }: { appId: string }) {
  const [tariffs, setTariffs] = useState<Tariff[]>([]);
  const [tariffsAppId, setTariffsAppId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [errorAppId, setErrorAppId] = useState<string | null>(null);
  const [workflowAppId, setWorkflowAppId] = useState(appId);
  const [reconciliationNotice, setReconciliationNotice] = useState<{ appId: string; message: string } | null>(null);

  // Concurrency & Scope ref for list loading
  const abortControllerRef = useRef<AbortController | null>(null);
  const reqIdRef = useRef<number>(0);

  // Create form state
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [createCode, setCreateCode] = useState<string>("");
  const [createName, setCreateName] = useState<string>("");
  const [createPrice, setCreatePrice] = useState<string>("");
  const [createCurrency, setCreateCurrency] = useState<string>("RUB");
  const [createPending, setCreatePending] = useState<boolean>(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const createScopeRef = useRef<{ appId: string; requestId: number } | null>(null);
  const createRequestIdRef = useRef(0);
  const createPendingRef = useRef(false);
  const createRecoveryRef = useRef<PendingCreateRecovery | null>(null);

  // Edit form state
  const [editingTariffId, setEditingTariffId] = useState<string | null>(null);
  const [editName, setEditName] = useState<string>("");
  const [editPrice, setEditPrice] = useState<string>("");
  const [editCurrency, setEditCurrency] = useState<string>("RUB");
  const [editIsActive, setEditIsActive] = useState<boolean>(true);
  const [editPending, setEditPending] = useState<boolean>(false);
  const [editError, setEditError] = useState<string | null>(null);
  const editScopeRef = useRef<{ appId: string; tariffId: string; requestId: number } | null>(null);
  const editRequestIdRef = useRef(0);
  const editPendingRef = useRef(false);
  const editRecoveryRef = useRef<PendingEditRecovery | null>(null);

  // Delete state
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deletePending, setDeletePending] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<{ tariffId: string; message: string } | null>(null);
  const deleteScopeRef = useRef<{ appId: string; tariffId: string; requestId: number } | null>(null);
  const deleteRequestIdRef = useRef(0);
  const deletePendingRef = useRef(false);
  const deleteRecoveryRef = useRef<PendingDeleteRecovery | null>(null);

  const visibleTariffs = tariffsAppId === appId ? tariffs : [];
  const currentError = errorAppId === appId ? error : null;
  const currentLoading = loading || (tariffsAppId !== appId && errorAppId !== appId);
  const createOutcomeUnknown = createRecoveryRef.current?.appId === appId;
  const editOutcomeUnknown = editRecoveryRef.current?.appId === appId && editRecoveryRef.current.tariffId === editingTariffId;
  const deleteOutcomeUnknown = deleteRecoveryRef.current?.appId === appId;
  const mutationOutcomeUnknown = createOutcomeUnknown || editOutcomeUnknown || deleteOutcomeUnknown;

  const loadTariffs = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const reqId = ++reqIdRef.current;

    setLoading(true);
    setError(null);

    request<unknown>(`/api/v1/apps/${encodeURIComponent(appId)}/tariffs`, {
      signal: controller.signal,
    })
      .then((raw) => {
        if (controller.signal.aborted || reqId !== reqIdRef.current) return;
        if (!Array.isArray(raw)) {
          throw new Error("Некорректный формат ответа: ожидался массив тарифов");
        }
        const validTariffs: Tariff[] = [];
        for (const item of raw) {
          if (isValidTariff(item)) {
            validTariffs.push(item);
          }
        }
        setTariffs(validTariffs);
        setTariffsAppId(appId);
        setErrorAppId(null);
        setLoading(false);
      })
      .catch((err) => {
        if (
          controller.signal.aborted ||
          (err as any)?.name === "AbortError" ||
          (err instanceof DOMException && err.name === "AbortError")
        ) {
          return;
        }
        if (reqId !== reqIdRef.current) return;
        setLoading(false);
        setErrorAppId(appId);
        setError(
          err instanceof ApiError
            ? err.message
            : err instanceof Error
            ? err.message
            : "Не удалось загрузить тарифы"
        );
      });
  }, [appId]);

  const invalidateTariffsRequest = () => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    reqIdRef.current += 1;
    setLoading(false);
  };

  useEffect(() => {
    // Reset all sub-workflows on appId change
    setWorkflowAppId(appId);
    setReconciliationNotice(null);
    setIsCreating(false);
    setCreateCode("");
    setCreateName("");
    setCreatePrice("");
    setCreateCurrency("RUB");
    setCreatePending(false);
    setCreateError(null);
    createRequestIdRef.current += 1;
    createPendingRef.current = false;
    createScopeRef.current = null;
    createRecoveryRef.current = null;

    setEditingTariffId(null);
    setEditName("");
    setEditPrice("");
    setEditCurrency("RUB");
    setEditIsActive(true);
    setEditPending(false);
    setEditError(null);
    editRequestIdRef.current += 1;
    editPendingRef.current = false;
    editScopeRef.current = null;
    editRecoveryRef.current = null;

    setDeleteConfirmId(null);
    setDeletePending(false);
    setDeleteError(null);
    deleteRequestIdRef.current += 1;
    deletePendingRef.current = false;
    deleteScopeRef.current = null;
    deleteRecoveryRef.current = null;

    loadTariffs();

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [appId, loadTariffs]);

  const fetchReconciliationList = async (
    targetAppId: string,
    isCurrent: () => boolean
  ): Promise<Tariff[] | null> => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    const listRequestId = ++reqIdRef.current;
    setLoading(false);
    const raw = await request<unknown>(`/api/v1/apps/${encodeURIComponent(targetAppId)}/tariffs`);
    if (reqIdRef.current !== listRequestId || !isCurrent()) return null;
    if (!Array.isArray(raw) || raw.some((item) => !isValidTariff(item))) {
      throw new Error("Некорректный ответ сервера при проверке результата тарифа");
    }
    const authoritative = raw as Tariff[];
    setTariffs(authoritative);
    setTariffsAppId(targetAppId);
    setError(null);
    setErrorAppId(null);
    return authoritative;
  };

  const isCreateCurrent = (operation: PendingCreateRecovery) =>
    createRecoveryRef.current === operation &&
    createScopeRef.current?.appId === operation.appId &&
    createScopeRef.current.requestId === operation.requestId;

  const reconcileCreate = async (operation: PendingCreateRecovery) => {
    try {
      const authoritative = await fetchReconciliationList(operation.appId, () => isCreateCurrent(operation));
      if (!authoritative || !isCreateCurrent(operation)) return;
      const exactCandidates = authoritative.filter((item) =>
        item.code === operation.code &&
        item.name === operation.name &&
        item.price === operation.price &&
        item.currency === operation.currency
      );
      const newCandidates = operation.preexistingIds === null
        ? []
        : exactCandidates.filter((item) => !operation.preexistingIds!.includes(item.id));
      if (newCandidates.length === 1 && exactCandidates.length === 1) {
        createRecoveryRef.current = null;
        setReconciliationNotice({ appId: operation.appId, message: "Тариф с указанными параметрами найден на сервере. Подтверждение исходной операции не получено." });
        setCreateCode("");
        setCreateName("");
        setCreatePrice("");
        setCreateCurrency("RUB");
        setCreateError(null);
        setIsCreating(false);
        return;
      }
      setCreateError("Результат создания неизвестен: сервер не подтвердил тариф с точными исходными параметрами. Повторите проверку.");
    } catch (err) {
      if (!isCreateCurrent(operation)) return;
      setCreateError(`Результат создания неизвестен; проверить список тарифов не удалось: ${err instanceof Error ? err.message : "ошибка запроса"}`);
    }
  };

  const isEditCurrent = (operation: PendingEditRecovery) =>
    editRecoveryRef.current === operation &&
    editScopeRef.current?.appId === operation.appId &&
    editScopeRef.current.tariffId === operation.tariffId &&
    editScopeRef.current.requestId === operation.requestId;

  const reconcileEdit = async (operation: PendingEditRecovery) => {
    try {
      const authoritative = await fetchReconciliationList(operation.appId, () => isEditCurrent(operation));
      if (!authoritative || !isEditCurrent(operation)) return;
      const item = authoritative.find((tariff) => tariff.id === operation.tariffId);
      if (
        item && item.name === operation.name && item.price === operation.price &&
        item.currency === operation.currency && item.is_active === operation.is_active
      ) {
        editRecoveryRef.current = null;
        setReconciliationNotice({ appId: operation.appId, message: "Актуальный тариф уже содержит указанные параметры. Подтверждение исходной операции не получено." });
        setEditingTariffId(null);
        setEditError(null);
        return;
      }
      operation.retryAllowed = true;
      setEditError(`Результат изменения неизвестен: сервер не подтвердил все исходные параметры. ${operation.reason}. Проверьте результат или повторите изменение.`);
    } catch (err) {
      if (!isEditCurrent(operation)) return;
      operation.retryAllowed = false;
      setEditError(`Результат изменения неизвестен; проверить список тарифов не удалось: ${err instanceof Error ? err.message : "ошибка запроса"}. ${operation.reason}`);
    }
  };

  const isDeleteCurrent = (operation: PendingDeleteRecovery, requestId: number) =>
    deleteRecoveryRef.current === operation &&
    deleteScopeRef.current?.appId === operation.appId &&
    deleteScopeRef.current.tariffId === operation.tariffId &&
    deleteScopeRef.current.requestId === requestId;

  const reconcileDelete = async (operation: PendingDeleteRecovery, requestId: number) => {
    try {
      const authoritative = await fetchReconciliationList(operation.appId, () => isDeleteCurrent(operation, requestId));
      if (!authoritative || !isDeleteCurrent(operation, requestId)) return;
      if (!authoritative.some((item) => item.id === operation.tariffId)) {
        deleteRecoveryRef.current = null;
        setReconciliationNotice({ appId: operation.appId, message: "Тариф отсутствует в актуальном списке. Подтверждение исходного удаления не получено." });
        setDeleteConfirmId(null);
        setDeleteError(null);
        return;
      }
      operation.retryAllowed = true;
      setDeleteError({
        tariffId: operation.tariffId,
        message: `Результат удаления неизвестен: тариф всё ещё отображается. ${operation.reason}. Проверьте результат или повторите удаление.`,
      });
    } catch (err) {
      if (!isDeleteCurrent(operation, requestId)) return;
      operation.retryAllowed = false;
      setDeleteError({
        tariffId: operation.tariffId,
        message: `Результат удаления неизвестен; проверить список тарифов не удалось: ${err instanceof Error ? err.message : "ошибка запроса"}. ${operation.reason}`,
      });
    }
  };

  // Create Handlers
  const handleCreateSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (createPendingRef.current) return;

    const unresolved = createRecoveryRef.current;
    if (unresolved?.appId === appId) {
      const requestId = ++createRequestIdRef.current;
      const recovery = { ...unresolved, requestId };
      createRecoveryRef.current = recovery;
      createScopeRef.current = { appId, requestId };
      createPendingRef.current = true;
      setCreatePending(true);
      setCreateError(null);
      try {
        await reconcileCreate(recovery);
      } finally {
        if (isCreateCurrent(recovery)) {
          createPendingRef.current = false;
          setCreatePending(false);
        } else if (createScopeRef.current?.appId === appId && createScopeRef.current.requestId === requestId) {
          createPendingRef.current = false;
          setCreatePending(false);
        }
      }
      return;
    }

    const trimmedCode = createCode.trim();
    const trimmedName = createName.trim();
    const trimmedCurrency = (createCurrency.trim() || "RUB").toUpperCase();

    if (!trimmedCode) {
      setCreateError("Код тарифа обязателен");
      return;
    }
    if (trimmedCode.length > 100) {
      setCreateError("Код тарифа не должен превышать 100 символов");
      return;
    }
    if (!trimmedName) {
      setCreateError("Название тарифа обязательно");
      return;
    }
    if (trimmedName.length > 255) {
      setCreateError("Название тарифа не должно превышать 255 символов");
      return;
    }

    let parsedPrice = 0;
    if (createPrice.trim() !== "") {
      const num = Number(createPrice.trim());
      if (!Number.isSafeInteger(num) || num < 0 || num > MAX_TARIFF_PRICE) {
        setCreateError("Цена должна быть неотрицательным целым числом");
        return;
      }
      parsedPrice = num;
    }

    if (trimmedCurrency.length > 10) {
      setCreateError("Код валюты не должен превышать 10 символов");
      return;
    }

    const targetAppId = appId;
    const requestId = ++createRequestIdRef.current;
    const recovery: PendingCreateRecovery = {
      appId: targetAppId,
      requestId,
      code: trimmedCode,
      name: trimmedName,
      price: parsedPrice,
      currency: trimmedCurrency,
      preexistingIds: tariffsAppId === targetAppId && !loading ? tariffs.map((item) => item.id) : null,
    };
    createScopeRef.current = { appId: targetAppId, requestId };
    createPendingRef.current = true;
    setCreatePending(true);
    setCreateError(null);
    setReconciliationNotice(null);

    try {
      const created = await request<Tariff>(
        `/api/v1/apps/${encodeURIComponent(targetAppId)}/tariffs`,
        {
          method: "POST",
          body: JSON.stringify({
            code: trimmedCode,
            name: trimmedName,
            price: parsedPrice,
            currency: trimmedCurrency,
          }),
        }
      );

      if (!isValidTariff(created)) {
        throw new Error("Некорректное подтверждение создания тарифа");
      }

      // Scope check
      if (createScopeRef.current?.appId !== targetAppId || createScopeRef.current.requestId !== requestId) return;

      invalidateTariffsRequest();
      createRecoveryRef.current = null;
      setTariffs((prev) => tariffsAppId === targetAppId ? [...prev, created] : [created]);
      setTariffsAppId(targetAppId);
      setCreateCode("");
      setCreateName("");
      setCreatePrice("");
      setCreateCurrency("RUB");
      setCreateError(null);
      setIsCreating(false);
    } catch (err) {
      if (createScopeRef.current?.appId !== targetAppId || createScopeRef.current.requestId !== requestId) return;
      if (err instanceof ApiError && [400, 409, 422].includes(err.status)) {
        // These API responses are definite rejections, so the operator can correct
        // the form and submit a new intent without reconciling an ambiguous write.
        createRecoveryRef.current = null;
        setCreateError(err.message);
        return;
      }
      createRecoveryRef.current = recovery;
      setCreateError(`Результат создания неизвестен: ${err instanceof ApiError ? err.message : err instanceof Error ? err.message : "ошибка запроса"}. Проверяем сохранение тарифа.`);
      await reconcileCreate(recovery);
    } finally {
      if (createScopeRef.current?.appId === targetAppId && createScopeRef.current.requestId === requestId) {
        createPendingRef.current = false;
        setCreatePending(false);
      }
    }
  };

  // Edit Handlers
  const handleStartEdit = (tariff: Tariff) => {
    setEditingTariffId(tariff.id);
    setEditName(tariff.name);
    setEditPrice(tariff.price !== null && tariff.price !== undefined ? String(tariff.price) : "");
    setEditCurrency(tariff.currency);
    setEditIsActive(tariff.is_active);
    setEditError(null);
  };

  const handleEditSubmit = async (e?: React.FormEvent, retryMutation = false) => {
    if (e) e.preventDefault();
    if (editPendingRef.current || !editingTariffId) return;

    const unresolved = editRecoveryRef.current;
    if (unresolved?.appId === appId && unresolved.tariffId === editingTariffId) {
      const requestId = ++editRequestIdRef.current;
      const recovery = { ...unresolved, requestId };
      editRecoveryRef.current = recovery;
      editScopeRef.current = { appId, tariffId: recovery.tariffId, requestId };
      editPendingRef.current = true;
      setEditPending(true);
      setEditError(null);
      try {
        await reconcileEdit(recovery);
        if (retryMutation && isEditCurrent(recovery) && recovery.retryAllowed) {
          // The authoritative GET still lacks the requested absolute state.
          // A fresh explicit PATCH retry can now use the unchanged form values.
          editRecoveryRef.current = null;
          editPendingRef.current = false;
          await handleEditSubmit();
        }
      } finally {
        if (editScopeRef.current?.appId === appId && editScopeRef.current.tariffId === recovery.tariffId && editScopeRef.current.requestId === requestId) {
          editPendingRef.current = false;
          setEditPending(false);
        }
      }
      return;
    }

    const trimmedName = editName.trim();
    const trimmedCurrency = (editCurrency.trim() || "RUB").toUpperCase();

    if (!trimmedName) {
      setEditError("Название тарифа обязательно");
      return;
    }
    if (trimmedName.length > 255) {
      setEditError("Название тарифа не должно превышать 255 символов");
      return;
    }

    let parsedPrice = 0;
    if (editPrice.trim() !== "") {
      const num = Number(editPrice.trim());
      if (!Number.isSafeInteger(num) || num < 0 || num > MAX_TARIFF_PRICE) {
        setEditError("Цена должна быть неотрицательным целым числом");
        return;
      }
      parsedPrice = num;
    }

    if (trimmedCurrency.length > 10) {
      setEditError("Код валюты не должен превышать 10 символов");
      return;
    }

    const targetAppId = appId;
    const targetTariffId = editingTariffId;
    const requestId = ++editRequestIdRef.current;
    editPendingRef.current = true;
    editScopeRef.current = { appId: targetAppId, tariffId: targetTariffId, requestId };
    setEditPending(true);
    setEditError(null);
    setReconciliationNotice(null);

    try {
      const updated = await request<Tariff>(
        `/api/v1/apps/${encodeURIComponent(targetAppId)}/tariffs/${encodeURIComponent(targetTariffId)}`,
        {
          method: "PATCH",
          body: JSON.stringify({
            name: trimmedName,
            price: parsedPrice,
            currency: trimmedCurrency,
            is_active: editIsActive,
          }),
        }
      );

      if (!isValidTariff(updated) || updated.id !== targetTariffId) {
        throw new Error("Некорректное подтверждение обновления тарифа");
      }

      // Scope check
      if (
        editScopeRef.current?.appId !== targetAppId ||
        editScopeRef.current?.tariffId !== targetTariffId ||
        editScopeRef.current?.requestId !== requestId
      ) {
        return;
      }

      invalidateTariffsRequest();
      editRecoveryRef.current = null;
      setTariffs((prev) =>
        prev.map((t) => (t.id === targetTariffId ? updated : t))
      );
      setTariffsAppId(targetAppId);
      setEditingTariffId(null);
      setEditError(null);
    } catch (err) {
      if (
        editScopeRef.current?.appId !== targetAppId ||
        editScopeRef.current?.tariffId !== targetTariffId ||
        editScopeRef.current?.requestId !== requestId
      ) {
        return;
      }
      if (err instanceof ApiError && [400, 422].includes(err.status)) {
        editRecoveryRef.current = null;
        setEditError(err.message);
        return;
      }
      const recovery: PendingEditRecovery = {
        appId: targetAppId,
        tariffId: targetTariffId,
        requestId,
        name: trimmedName,
        price: parsedPrice,
        currency: trimmedCurrency,
        is_active: editIsActive,
        reason: err instanceof ApiError ? err.message : err instanceof Error ? err.message : "ошибка запроса",
        retryAllowed: false,
      };
      editRecoveryRef.current = recovery;
      await reconcileEdit(recovery);
    } finally {
      if (
        editScopeRef.current?.appId === targetAppId &&
        editScopeRef.current?.tariffId === targetTariffId &&
        editScopeRef.current?.requestId === requestId
      ) {
        editPendingRef.current = false;
        setEditPending(false);
      }
    }
  };

  // Delete Handlers
  const handleDeleteConfirm = async (tariff: Tariff, retryMutation = false) => {
    if (deletePendingRef.current) return;
    const targetAppId = appId;
    const targetTariffId = tariff.id;
    const unresolved = deleteRecoveryRef.current;
    const recovering = unresolved?.appId === targetAppId && unresolved.tariffId === targetTariffId;
    const recovery: PendingDeleteRecovery = recovering ? unresolved : { appId: targetAppId, tariffId: targetTariffId, reason: "", retryAllowed: false };
    const requestId = ++deleteRequestIdRef.current;
    deletePendingRef.current = true;
    deleteScopeRef.current = { appId: targetAppId, tariffId: targetTariffId, requestId };
    setDeletePending(true);
    setDeleteError(null);
    setReconciliationNotice(null);

    try {
      if (recovering) {
        await reconcileDelete(recovery, requestId);
        if (retryMutation && isDeleteCurrent(recovery, requestId) && recovery.retryAllowed) {
          deleteRecoveryRef.current = null;
          deletePendingRef.current = false;
          await handleDeleteConfirm(tariff);
        }
        return;
      }
      await request<void>(
        `/api/v1/apps/${encodeURIComponent(targetAppId)}/tariffs/${encodeURIComponent(targetTariffId)}`,
        {
          method: "DELETE",
        }
      );

      // Scope check
      if (
        deleteScopeRef.current?.appId !== targetAppId ||
        deleteScopeRef.current?.tariffId !== targetTariffId ||
        deleteScopeRef.current?.requestId !== requestId
      ) {
        return;
      }

      invalidateTariffsRequest();
      deleteRecoveryRef.current = null;
      setTariffs((prev) => prev.filter((t) => t.id !== targetTariffId));
      setTariffsAppId(targetAppId);
      setDeleteConfirmId(null);
      setDeleteError(null);
    } catch (err) {
      if (
        deleteScopeRef.current?.appId !== targetAppId ||
        deleteScopeRef.current?.tariffId !== targetTariffId ||
        deleteScopeRef.current?.requestId !== requestId
      ) {
        return;
      }
      recovery.reason = err instanceof ApiError ? err.message : err instanceof Error ? err.message : "ошибка запроса";
      recovery.retryAllowed = false;
      deleteRecoveryRef.current = recovery;
      await reconcileDelete(recovery, requestId);
    } finally {
      if (
        deleteScopeRef.current?.appId === targetAppId &&
        deleteScopeRef.current?.tariffId === targetTariffId &&
        deleteScopeRef.current?.requestId === requestId
      ) {
        deletePendingRef.current = false;
        setDeletePending(false);
      }
    }
  };

  return (
    <div className="tariffs-workflow" style={{ marginTop: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <span style={{ fontSize: 14, color: "var(--omni-text-secondary)" }}>
          Всего тарифов: {visibleTariffs.length}
        </span>
        <Button
          variant="primary"
          size="sm"
          disabled={createPending || createOutcomeUnknown}
          onClick={() => {
            if (createPendingRef.current) return;
            setIsCreating((prev) => !prev);
            setCreateError(null);
          }}
        >
          {isCreating ? "Отменить добавление" : "Добавить тариф"}
        </Button>
      </div>

      <p className="text-secondary" style={{ margin: "0 0 12px", fontSize: 13 }}>
        Все цены указаны в минимальных единицах валюты; для RUB — в копейках.
      </p>

      {workflowAppId === appId && isCreating && (
        <Card style={{ padding: 16, marginBottom: 16, border: "1px solid var(--omni-primary)" }}>
          <form aria-label="Создание тарифа" onSubmit={handleCreateSubmit} noValidate>
            <h4 style={{ margin: "0 0 12px" }}>Новый тариф</h4>
            <div style={{ display: "grid", gap: 12 }}>
              <div>
                <label htmlFor="create-tariff-code" style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
                  Код тарифа *
                </label>
                <Input
                  id="create-tariff-code"
                  placeholder="pro-monthly"
                  value={createCode}
                  onChange={(e) => setCreateCode(e.target.value)}
                  disabled={createPending || createOutcomeUnknown}
                />
              </div>

              <div>
                <label htmlFor="create-tariff-name" style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
                  Название *
                </label>
                <Input
                  id="create-tariff-name"
                  placeholder="Pro ежемесячный"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  disabled={createPending || createOutcomeUnknown}
                />
              </div>

              <div>
                <label htmlFor="create-tariff-price" style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
                  Цена в минимальных единицах валюты (для RUB — копейки; пусто для бесплатного)
                </label>
                <Input
                  id="create-tariff-price"
                  type="number"
                  min="0"
                  max={MAX_TARIFF_PRICE}
                  step="1"
                  placeholder="0"
                  value={createPrice}
                  onChange={(e) => setCreatePrice(e.target.value)}
                  disabled={createPending || createOutcomeUnknown}
                />
                <small>Максимальная цена: {MAX_TARIFF_PRICE}</small>
              </div>

              <div>
                <label htmlFor="create-tariff-currency" style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
                  Валюта
                </label>
                <Input
                  id="create-tariff-currency"
                  placeholder="RUB"
                  value={createCurrency}
                  onChange={(e) => setCreateCurrency(e.target.value)}
                  disabled={createPending || createOutcomeUnknown}
                />
              </div>
            </div>

            {createError && (
              <p role="alert" className="error-text" style={{ marginTop: 12 }}>
                {createError}{" "}
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={createPending}
                  onClick={() => handleCreateSubmit()}
                >
                  {createOutcomeUnknown ? "Проверить результат" : "Повторить"}
                </Button>
              </p>
            )}

            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <Button
                variant="primary"
                size="sm"
                type="submit"
                disabled={createPending}
              >
                {createPending ? (createOutcomeUnknown ? "Проверка…" : "Создание…") : createOutcomeUnknown ? "Проверить результат" : "Сохранить тариф"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                disabled={createPending || createOutcomeUnknown}
                onClick={() => {
                  setIsCreating(false);
                  setCreateError(null);
                }}
              >
                Отмена
              </Button>
            </div>
          </form>
        </Card>
      )}

      {currentError && (
        <p role="alert" className="error-text">
          {currentError}{" "}
          <Button variant="secondary" size="sm" onClick={loadTariffs}>
            Повторить
          </Button>
        </p>
      )}

      {currentLoading && <p role="status">Загрузка тарифов…</p>}

      {reconciliationNotice?.appId === appId && (
        <p role="status" className="text-secondary">{reconciliationNotice.message}</p>
      )}

      {!currentLoading && !currentError && visibleTariffs.length === 0 && (
        <p>Нет тарифов.</p>
      )}

      <div style={{ display: "grid", gap: 10 }}>
        {workflowAppId === appId && visibleTariffs.map((t) => {
          const isEditing = editingTariffId === t.id;
          const isConfirmingDelete = deleteConfirmId === t.id;
          const isItemDeleting = deletePending && deleteConfirmId === t.id;
          const hasItemDeleteError = deleteError?.tariffId === t.id;

          if (isEditing) {
            return (
              <Card key={t.id} style={{ padding: 16, border: "1px solid var(--omni-primary)" }}>
                <form
                  aria-label={`Редактирование тарифа ${t.name}`}
                  onSubmit={handleEditSubmit}
                  noValidate
                >
                  <h4 style={{ margin: "0 0 12px" }}>
                    Редактирование: <code>{t.code}</code>
                  </h4>
                  <div style={{ display: "grid", gap: 12 }}>
                    <div>
                      <label htmlFor={`edit-tariff-name-${t.id}`} style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
                        Название *
                      </label>
                      <Input
                        id={`edit-tariff-name-${t.id}`}
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        disabled={editPending || editOutcomeUnknown}
                      />
                    </div>

                    <div>
                      <label htmlFor={`edit-tariff-price-${t.id}`} style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
                        Цена в минимальных единицах валюты (для RUB — копейки; пусто для бесплатного)
                      </label>
                      <Input
                        id={`edit-tariff-price-${t.id}`}
                        type="number"
                        min="0"
                        max={MAX_TARIFF_PRICE}
                        step="1"
                        value={editPrice}
                        onChange={(e) => setEditPrice(e.target.value)}
                        disabled={editPending || editOutcomeUnknown}
                      />
                      <small>Максимальная цена: {MAX_TARIFF_PRICE}</small>
                    </div>

                    <div>
                      <label htmlFor={`edit-tariff-currency-${t.id}`} style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
                        Валюта
                      </label>
                      <Input
                        id={`edit-tariff-currency-${t.id}`}
                        value={editCurrency}
                        onChange={(e) => setEditCurrency(e.target.value)}
                        disabled={editPending || editOutcomeUnknown}
                      />
                    </div>

                    <div>
                      <label
                        htmlFor={`edit-tariff-active-${t.id}`}
                        style={{ display: "inline-flex", gap: 8, alignItems: "center", cursor: "pointer", fontSize: 14 }}
                      >
                        <input
                          id={`edit-tariff-active-${t.id}`}
                          type="checkbox"
                          checked={editIsActive}
                          onChange={(e) => setEditIsActive(e.target.checked)}
                          disabled={editPending || editOutcomeUnknown}
                        />
                        <span>Активен</span>
                      </label>
                    </div>
                  </div>

                  {editError && (
                    <p role="alert" className="error-text" style={{ marginTop: 12 }}>
                      {editError}{" "}
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={editPending}
                        onClick={() => handleEditSubmit()}
                      >
                        {editOutcomeUnknown ? "Проверить результат" : "Повторить"}
                      </Button>
                      {editOutcomeUnknown && editRecoveryRef.current?.retryAllowed && (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={editPending}
                          onClick={() => handleEditSubmit(undefined, true)}
                        >
                          Повторить
                        </Button>
                      )}
                    </p>
                  )}

                  <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                    <Button
                      variant="primary"
                      size="sm"
                      type="submit"
                      disabled={editPending}
                    >
                      {editPending ? (editOutcomeUnknown ? "Проверка…" : "Сохранение…") : editOutcomeUnknown ? "Проверить результат" : "Сохранить"}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      type="button"
                      disabled={editPending || editOutcomeUnknown}
                      onClick={() => {
                        setEditingTariffId(null);
                        setEditError(null);
                      }}
                    >
                      Отмена
                    </Button>
                  </div>
                </form>
              </Card>
            );
          }

          return (
            <Card key={t.id} role="region" aria-label={`Тариф ${t.name}`} style={{ padding: 14 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 12,
                }}
              >
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <strong style={{ fontSize: 15 }}>{t.name}</strong>
                    <Badge variant={t.is_active ? "success" : "neutral"} size="sm">
                      {t.is_active ? "Активен" : "Неактивен"}
                    </Badge>
                  </div>
                  <div style={{ fontSize: 13, color: "var(--omni-text-secondary)" }}>
                    Код: <code>{t.code}</code> · Стоимость:{" "}
                    {t.price === null || t.price === undefined ? (
                      <>
                        <span>Цена не задана</span>{" "}
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={deletePending || editPending || mutationOutcomeUnknown}
                          onClick={() => {
                            handleStartEdit(t);
                            setEditPrice("0");
                          }}
                        >
                          Бесплатно
                        </Button>
                      </>
                    ) : (
                      <span>{formatPrice(t.price, t.currency)}</span>
                    )}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={deletePending || editPending || mutationOutcomeUnknown}
                    onClick={() => handleStartEdit(t)}
                  >
                    Редактировать
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={deletePending || editPending || mutationOutcomeUnknown}
                    onClick={() => {
                      setDeleteConfirmId(t.id);
                      setDeleteError(null);
                    }}
                  >
                    Удалить
                  </Button>
                </div>
              </div>

              {isConfirmingDelete && (
                <div
                  role="group"
                  aria-label={`Подтверждение удаления тарифа ${t.name}`}
                  style={{
                    marginTop: 12,
                    padding: 10,
                    borderRadius: 6,
                    background: "var(--omni-surface-raised, #f9fafb)",
                    border: "1px solid var(--omni-border-subtle)",
                  }}
                >
                  <p style={{ margin: "0 0 8px", fontSize: 13 }}>
                    Удалить тариф {t.name}? Это действие необратимо.
                  </p>
                  <div style={{ display: "flex", gap: 8 }}>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={deletePending}
                      onClick={() => handleDeleteConfirm(t)}
                    >
                      {isItemDeleting ? (deleteOutcomeUnknown ? "Проверка…" : "Удаление…") : deleteOutcomeUnknown ? "Проверить результат" : "Подтвердить удаление"}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={deletePending || deleteOutcomeUnknown}
                      onClick={() => {
                        setDeleteConfirmId(null);
                        setDeleteError(null);
                      }}
                    >
                      Отмена
                    </Button>
                  </div>
                </div>
              )}

              {hasItemDeleteError && (
                <p role="alert" className="error-text" style={{ marginTop: 8 }}>
                  {deleteError.message}{" "}
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={deletePending}
                    onClick={() => handleDeleteConfirm(t)}
                  >
                    {deleteOutcomeUnknown ? "Проверить результат" : "Повторить"}
                  </Button>
                  {deleteOutcomeUnknown && deleteRecoveryRef.current?.retryAllowed && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={deletePending}
                      onClick={() => handleDeleteConfirm(t, true)}
                    >
                      Повторить
                    </Button>
                  )}
                </p>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

export default Tariffs;
